import React, { useContext } from 'react';

import { media } from 'context';
import Clickable from 'ds/Clickable';
import Icon from 'ds/Icon';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import Link from 'ds/Link';
import { StickyContainer } from 'ds/StickyContainer';
import Text from 'ds/Text';
import { useBackNavigation } from 'hooks';
import { IconName } from 'shared';

export type SubmenuLayoutItem = {
  label: string;
  iconName: IconName;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
};

interface SubmenuLayoutProps {
  backHref: string;
  title?: string | null;
  mobileTitle?: string | null;
  menuItems: SubmenuLayoutItem[];
  children: React.ReactNode;
  maxWidth?: string | number | null;
}

const MENU_WIDTH = 256;
const CONTENT_MAX_WIDTH = 1120;

const SubmenuLayout: React.FC<SubmenuLayoutProps> = ({
  backHref,
  title,
  mobileTitle,
  menuItems,
  children,
  maxWidth = CONTENT_MAX_WIDTH
}) => {
  const { isMobile } = useContext(media);
  const headerTitle = mobileTitle || title;

  const hasMenuItems = Array.isArray(menuItems) && menuItems.length > 0;

  const renderMenuItems = () => {
    if (!hasMenuItems) {
      return null;
    }

    const nodes = menuItems.map(item => {
      const isActive = !!item.isActive;
      const itemContent = (
        <Layout
          direction="row"
          align="center"
          gap={12}
          paddingX={16}
          paddingY={12}
          borderRadius={12}
          color={isActive ? 'blue-100' : 'white'}
          hoverColor={isActive ? 'blue-100' : 'gray-25'}
        >
          <Icon name={item.iconName} size={20} stroke={2} />
          <Text size="body-sm" color="gray-900" semibold>
            {item.label}
          </Text>
        </Layout>
      );

      if (item.href) {
        return (
          <Link key={item.label} href={item.href} fullWidth>
            {itemContent}
          </Link>
        );
      }

      if (item.onClick) {
        return (
          <Clickable key={item.label} onClick={item.onClick} fullWidth>
            {itemContent}
          </Clickable>
        );
      }

      return (
        <Layout key={item.label} width="100%">
          {itemContent}
        </Layout>
      );
    });

    return (
      <Layout direction="column" gap={8} width={isMobile ? '100%' : MENU_WIDTH} flexShrink={0}>
        {nodes}
      </Layout>
    );
  };

  const menuNode = renderMenuItems();

  const handleBack = useBackNavigation(backHref);

  return (
    <Layout flex direction="column" width="100%" height="100%" color="white">
      <StickyContainer zIndex={1000}>
        <Layout
          width="100%"
          height={isMobile ? 60 : 88}
          background="white"
          {...(isMobile
            ? { paddingTop: 12, paddingRight: 45, flex: true, align: 'center' }
            : { paddingX: 40, paddingY: 24, borderBottom: true, borderColor: 'gray-50' })}
        >
          <IconButton
            size="lg"
            type="noBackground"
            name={isMobile ? 'leftChevron' : 'leftArrow'}
            onClick={handleBack}
            href={backHref}
          />
          {isMobile && (
            <Layout flex flexGrow={1} align="center" justify="center">
              <Text size="headline-sm">{headerTitle}</Text>
            </Layout>
          )}
        </Layout>
      </StickyContainer>
      <Layout
        direction="column"
        gap={36}
        width="100%"
        paddingX={isMobile ? 24 : 40}
        paddingY={isMobile ? 24 : 40}
        flexGrow={1}
      >
        <Layout
          direction={isMobile || !menuNode ? 'column' : 'row'}
          gap={isMobile || !menuNode ? 24 : 40}
          width="100%"
          align={!menuNode ? 'center' : undefined}
        >
          {menuNode}

          <Layout
            flexGrow={!menuNode ? 0 : 1}
            width={!menuNode ? undefined : '100%'}
            maxWidth={maxWidth || undefined}
            align={!menuNode ? 'center' : undefined}
          >
            {!isMobile && title && (
              <Text size="headline-lg" color="gray-900">
                {title}
              </Text>
            )}
            {children}
          </Layout>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default SubmenuLayout;
